import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

import { ClosableModalWithErrorResponse } from './../base/closable-modal-with-error-response-model';
import { ModalService, ConfirmationModalComponent } from '../../modules/modal';
import { NotificationService } from '../../services';

import { ImportMappingsPayload, AllowedImportField, ImportData, Message } from '../../models';
import { TranslationConstants } from '../../constants';

export abstract class FileUploadModalBaseComponent extends ClosableModalWithErrorResponse {
  private allowedFileTypes = ['xls', 'xlsx'];
  fileTypeInvalid: boolean;
  fileToUpload: File;
  uploaded: boolean;
  showLoader: boolean; // used to display loader while waiting for response
  columns: ImportData[] = [];
  allowedFields: AllowedImportField[] = []; // holds all allowed fields
  filteredAllowedFields: AllowedImportField[] = []; // filtered out selected ones
  selectedFields: string[] = [];
  fileHash: string; // file identifier for import confirmation after mapping is submitted

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    protected translate: TranslateService
  ) {
    super(notificationService, modalService);
    this.listenForErrorMessages();
  }

  // uploads file with import data and handles response for displaying mappings
  abstract upload(): void;
  // submits mappings which triggers actual import process
  abstract startImport(): void;

  back(): void {
    this.uploaded = false;
    this.fileToUpload = undefined;
    this.selectedFields = [];
    this.responseErrors = [];
  }

  handleFileUpload(files: FileList): void {
    this.resetErrors();
    this.fileTypeInvalid = false;
    this.fileToUpload = files.item(0);
    const nameSplit = this.fileToUpload.name.split('.');
    const extension = nameSplit[nameSplit.length - 1];
    if (!this.allowedFileTypes.includes(extension)) {
      this.fileTypeInvalid = true;
      this.responseErrors.push(TranslationConstants.ERROR_FILE_TYPE_INVALID);
    }
  }

  // override
  close(): void {
    if (this.uploaded && (this.showLoader || this.columns.filter((col: ImportData) => !!col.field).length !== 0)) {
      this.modalService.openModal({
        component: ConfirmationModalComponent,
        data: {
          title: TranslationConstants.GLOBAL_LEAVE,
          content: TranslationConstants.GLOBAL_LEAVE_CONTENT,
        } as Message,
        success: () => {
          this.closeAllModals();
        },
      });
      return;
    }
    this.responseErrors = [];
    this.closeAllModals();
  }

  // override
  protected handleErrors({ error, status }: HttpErrorResponse): void {
    this.showLoader = false;
    if (status === 400) {
      try {
        // in case we receive array of error objects from the API
        const errors = JSON.parse(error.errors.global);
        errors.forEach(({ err, subject }) => {
          const translated = this.translate.instant(
            TranslationConstants.ERROR_IMPORT_FIELD_INVALID,
            { value1: err.name, value2: subject.name, value3: err.message }
          );
          this.responseErrors.push(translated);
        });
        return;
      } catch {
        // if unable to parse, string error returned from the API
        // ignore
      }
      // this.responseErrors.push(error.errors.global);
    } else if (status === 422) {
      // add validation errors to errors array
      Object.keys(error.errors).forEach((key) => {
        this.responseErrors.push(`${key} - ${error.errors[key].msg}`);
      });
    } else {
      // this.responseErrors.push(TranslationConstants.GLOBAL_SERVER_ERROR);
    }
  }

  handleMappingSelected(column: ImportData, newValue: string): void {
    const oldValue = column.field;

    column.field = newValue;
    if (this.selectedFields.includes(newValue)) {
      this.selectedFields.push(newValue);
    }
    if (this.selectedFields.includes(oldValue)) {
      this.selectedFields = this.selectedFields.filter((data: string) => data !== oldValue);
    }
  }

  // public since it is used in template
  filterAllowedFields(column: ImportData): void {
    this.filteredAllowedFields = this.allowedFields.filter((importField) => {
      return !this.selectedFields.includes(importField.field) || importField.field === column.field;
    });
  }

  // public since it is used in template
  resetFilteredFields(): void {
    this.filteredAllowedFields = this.allowedFields;
  }

  protected prepareImportPayload(): ImportMappingsPayload {
    const dataCopy = JSON.parse(JSON.stringify(this.columns));
    const data = new ImportMappingsPayload();
    data.hash = this.fileHash;
    data.mappings = dataCopy.filter((col: ImportData) => !!col.field).map((col: ImportData) => {
      return col;
    });

    return data;
  }

  protected handleDefaultFieldsForHeaders(): void {
    this.columns.forEach((column) => {
      this.handleMappingSelected(
        column,
        this.filteredAllowedFields.find(filteredAllowedField => filteredAllowedField.defaultHeader === column.header) ? this.filteredAllowedFields.find(filteredAllowedField => filteredAllowedField.defaultHeader === column.header).field : ''
      );
    });
  }
}
